import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useFamily } from '../../context/FamilyContext';
import {
  subscribeToThreads,
  createThread,
  searchMessages,
  buildSearchSnippet,
} from '../../services/messageService';
import { MESSAGE_LINK_TYPES } from '@coparent/shared/src/utils/constants';
import { formatDate } from '@coparent/shared/src/utils/dateUtils';
import {
  HiChatBubbleLeftRight,
  HiPlus,
  HiMagnifyingGlass,
  HiOutlineReceiptPercent,
  HiOutlineCalendarDays,
  HiOutlineArrowsRightLeft,
} from 'react-icons/hi2';

// Debounce before a keystroke turns into a fan-out of Firestore reads. Thread
// titles filter instantly from memory; only the message-body search waits.
const SEARCH_DEBOUNCE_MS = 300;

const LINK_ICONS = {
  expense: HiOutlineReceiptPercent,
  activity: HiOutlineCalendarDays,
  swap: HiOutlineArrowsRightLeft,
};

/** Small badge showing what record a thread is attached to. */
const LinkedBadge = ({ linkedEntity }) => {
  if (!linkedEntity?.type) return null;
  const Icon = LINK_ICONS[linkedEntity.type] || HiOutlineReceiptPercent;
  const meta = MESSAGE_LINK_TYPES[linkedEntity.type];
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-accent-50 text-accent-700 text-[0.625rem] font-semibold flex-shrink-0"
      title={`Linked to ${meta?.label || linkedEntity.type}`}
    >
      <Icon className="text-[0.7rem]" />
      {meta?.label || linkedEntity.type}
    </span>
  );
};

const ThreadList = ({ activeThreadId, onSelectThread }) => {
  const { currentUser } = useAuth();
  const { familyId, family, familyChildren, childrenMap, getParticipantProfile } = useFamily();
  const [threads, setThreads] = useState([]);
  const [search, setSearch] = useState('');
  const [childFilter, setChildFilter] = useState('all');
  const [messageMatches, setMessageMatches] = useState([]);
  const [searching, setSearching] = useState(false);

  // Guards against an earlier, slower search overwriting a later one's results.
  const searchRunRef = useRef(0);

  useEffect(() => {
    if (!familyId) return;
    return subscribeToThreads(familyId, setThreads);
  }, [familyId]);

  // Threads matching the child filter — applied before search so the filter
  // narrows both the thread list and the message search.
  const childFilteredThreads = useMemo(() => {
    if (childFilter === 'all') return threads;
    return threads.filter((thread) => (thread.childIds || []).includes(childFilter));
  }, [threads, childFilter]);

  // Fast, local pass over thread metadata.
  const filteredThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return childFilteredThreads;
    return childFilteredThreads.filter((thread) => {
      const senderProfile = thread.lastMessageSenderId
        ? getParticipantProfile(thread.lastMessageSenderId)
        : null;
      return (
        (thread.title || '').toLowerCase().includes(q) ||
        (thread.lastMessageText || '').toLowerCase().includes(q) ||
        (senderProfile?.displayName || '').toLowerCase().includes(q)
      );
    });
  }, [childFilteredThreads, search, getParticipantProfile]);

  // Deeper pass over message BODIES. Debounced and run against the
  // child-filtered thread set so it never reads threads the user filtered out.
  useEffect(() => {
    const q = search.trim();
    if (!q || !familyId) {
      setMessageMatches([]);
      setSearching(false);
      return;
    }

    const runId = searchRunRef.current + 1;
    searchRunRef.current = runId;
    setSearching(true);

    const timer = setTimeout(async () => {
      try {
        const results = await searchMessages(familyId, childFilteredThreads, q);
        // Ignore results from a query the user has already typed past.
        if (searchRunRef.current === runId) setMessageMatches(results);
      } catch {
        if (searchRunRef.current === runId) setMessageMatches([]);
      } finally {
        if (searchRunRef.current === runId) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search, familyId, childFilteredThreads]);

  const isUnread = useCallback((thread) => {
    if (!currentUser?.uid) return false;
    const lastRead = thread.lastReadAt?.[currentUser.uid]?.toDate?.() || null;
    const lastMsg = thread.lastMessageAt?.toDate?.() || null;
    if (!lastMsg) return false;
    if (!lastRead) return true;
    return lastMsg > lastRead;
  }, [currentUser?.uid]);

  const handleNewThread = async () => {
    if (!familyId || !currentUser) return;
    try {
      const participantIds = family?.parentIds || [];
      const thread = await createThread(familyId, {
        createdBy: currentUser.uid,
        participantIds,
        // A thread created while a child filter is active inherits that tag —
        // the user has already told us who this conversation is about.
        childIds: childFilter === 'all' ? [] : [childFilter],
      });
      onSelectThread(thread.id, '');
    } catch (err) {
      // ignore
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return formatDate(timestamp, 'h:mm a');
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return formatDate(timestamp, 'EEE');
      return formatDate(timestamp, 'MMM d');
    } catch {
      return '';
    }
  };

  const searchActive = search.trim().length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-surface-100">
        <h2 className="text-lg font-bold text-ink-800">Conversations</h2>
        <button
          onClick={handleNewThread}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 hover:bg-accent-100 transition-colors"
          title="New conversation"
        >
          <HiPlus className="text-base" />
        </button>
      </div>

      {/* Search */}
      <div className="px-4 py-3 border-b border-surface-100">
        <div className="relative">
          <HiMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300 text-sm" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="text"
            placeholder="Search messages…"
            className="w-full pl-9 pr-3 py-2 bg-surface-50 border border-surface-200 rounded-full text-sm text-ink-800 placeholder:text-ink-300 focus:ring-2 focus:ring-accent-200 focus:border-accent-400 outline-none transition-all"
          />
        </div>

        {/* Child filter chips — only worth showing once there are children */}
        {familyChildren?.length > 0 && (
          <div className="flex gap-1.5 mt-2.5 overflow-x-auto pb-0.5">
            <button
              onClick={() => setChildFilter('all')}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                childFilter === 'all'
                  ? 'bg-ink-800 text-white'
                  : 'bg-surface-50 text-ink-500 hover:bg-surface-100'
              }`}
            >
              All
            </button>
            {familyChildren.map((child) => {
              const active = childFilter === child.id;
              const color = childrenMap?.[child.id]?.color;
              return (
                <button
                  key={child.id}
                  onClick={() => setChildFilter(active ? 'all' : child.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                    active
                      ? 'bg-ink-800 text-white'
                      : 'bg-surface-50 text-ink-500 hover:bg-surface-100'
                  }`}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color || '#B8B2A8' }}
                  />
                  {child.name?.split(' ')[0] || 'Child'}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto">
        {filteredThreads.length === 0 && messageMatches.length === 0 && !searching && (
          <div className="text-center text-ink-300 py-16 px-5">
            <HiChatBubbleLeftRight className="text-3xl mx-auto mb-2 opacity-40" />
            <p className="text-sm font-medium">{searchActive ? 'No matches' : 'No conversations yet'}</p>
            <p className="text-xs mt-1">
              {searchActive ? 'Try a different search term' : 'Start a new conversation'}
            </p>
          </div>
        )}

        {filteredThreads.map((thread) => {
          const unread = isUnread(thread);
          const isActive = thread.id === activeThreadId;
          const senderProfile = thread.lastMessageSenderId
            ? getParticipantProfile(thread.lastMessageSenderId)
            : null;
          const senderName = senderProfile?.displayName?.split(' ')[0] || '';
          const preview = thread.lastMessageText
            ? (senderName ? `${senderName}: ` : '') + thread.lastMessageText
            : 'No messages yet';
          const title = thread.title || 'New Conversation';
          const tags = (thread.childIds || [])
            .map((id) => childrenMap?.[id])
            .filter(Boolean);

          return (
            <button
              key={thread.id}
              onClick={() => onSelectThread(thread.id, thread.title || '')}
              className={`w-full text-left px-5 py-3.5 border-b border-surface-50 transition-colors flex items-start gap-3 ${
                isActive ? 'bg-accent-50/50' : 'hover:bg-surface-50'
              }`}
            >
              {/* Unread dot */}
              <div className="w-3 pt-2 flex-shrink-0">
                {unread && <div className="w-2.5 h-2.5 rounded-full bg-accent-500" />}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm truncate ${unread ? 'font-bold text-ink-800' : 'font-medium text-ink-700'}`}>
                    {title}
                  </span>
                  <span className="text-[0.625rem] text-ink-300 font-medium flex-shrink-0">
                    {formatTime(thread.lastMessageAt)}
                  </span>
                </div>
                <p className={`text-xs mt-0.5 truncate ${unread ? 'font-medium text-ink-600' : 'text-ink-400'}`}>
                  {preview}
                </p>
                {(thread.linkedEntity || tags.length > 0) && (
                  <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                    <LinkedBadge linkedEntity={thread.linkedEntity} />
                    {tags.map((child) => (
                      <span
                        key={child.id}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-surface-100 text-ink-500 text-[0.625rem] font-semibold"
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: child.color || '#B8B2A8' }}
                        />
                        {child.name?.split(' ')[0]}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </button>
          );
        })}

        {/* Message-body matches — the results thread titles alone can't surface */}
        {searchActive && (
          <div className="border-t border-surface-100">
            <div className="px-5 pt-3 pb-1 flex items-center justify-between">
              <p className="text-[0.625rem] font-bold uppercase tracking-wide text-ink-300">
                In messages
              </p>
              {searching && <span className="text-[0.625rem] text-ink-300">Searching…</span>}
            </div>

            {!searching && messageMatches.length === 0 && (
              <p className="px-5 pb-4 text-xs text-ink-300">No messages match “{search.trim()}”.</p>
            )}

            {messageMatches.map((match) => {
              const snippet = buildSearchSnippet(match.text, search);
              const senderProfile = getParticipantProfile(match.senderId);
              return (
                <button
                  key={`${match.threadId}-${match.id}`}
                  onClick={() => onSelectThread(match.threadId, match.threadTitle)}
                  className="w-full text-left px-5 py-3 border-b border-surface-50 hover:bg-surface-50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-ink-600 truncate">
                      {match.threadTitle}
                    </span>
                    <span className="text-[0.625rem] text-ink-300 font-medium flex-shrink-0">
                      {formatTime(match.createdAt)}
                    </span>
                  </div>
                  <p className="text-xs text-ink-400 mt-0.5 line-clamp-2">
                    {senderProfile?.displayName?.split(' ')[0] && (
                      <span className="font-medium text-ink-500">
                        {senderProfile.displayName.split(' ')[0]}:{' '}
                      </span>
                    )}
                    {snippet.before}
                    <mark className="bg-sunbeam/40 text-ink-800 rounded px-0.5">{snippet.match}</mark>
                    {snippet.after}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ThreadList;
